import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./css/Modales.css";
import "../tablas/css/Tablas.css";
import CloseButton from "../buttons/CloseButton";
import { renderizarValor } from "../../functions/dataFunctions";
import { cambio, sePuedeTocar } from "../tablas/celdaEditable";
import { ruta as rutaDeUnidad, tipoDeColeccion } from "../../shared/unidad";
import { updateRow } from "../../services/supabaseRealtime";
import { buscarFila } from "../../hooks/useItemPedido";
import { fichaContent } from "../fichas/data/FichaContent";
import Ficha from "../fichas/Ficha";
import Loading from "../../routes/Loading";
import FormGestor from "../formularios/FormGestor";
import { refreshCache } from "../../services/cache";
import {
  useFiltrosTabla,
  ES_COLUMNA_ESTADO,
  tonoDeFila,
} from "../tablas/useFiltrosTabla";
import FiltrosBar from "../tablas/FiltrosBar";
import ResumenBar from "../tablas/ResumenBar";
import EstadoPill from "../tablas/EstadoPill";
import ThOrden, {
  SIN_ORDEN,
  ordenarFilas,
  siguienteOrden,
} from "../tablas/ThOrden";
import Patente from "../funcionales/Patente";

/* «Furgones» → «furgón». El botón decía «AGREGAR» a secas y este listado se
 * abre desde ocho padrones y desde adentro de otras pantallas: sin el nombre
 * hay que mirar el título para saber qué va a crear.
 *
 * Es una tabla y no una regla de español: los ocho títulos son fijos, y un
 * `slice(0, -1)` daría «Personas» → «Persona» bien pero «Vehículos» →
 * «Vehículo» sin el acento y «Stock» → «Stoc». Lo que no está en la tabla
 * vuelve tal cual, en minúscula: peor es inventarle un singular.
 *
 * «Talleres y servicios» es el padrón de proveedores del Pañol con su nombre
 * del 20/10, y su botón sigue diciendo «+ proveedor»: la mayoría de las filas
 * son marcas y casas de repuestos (Scania, Michelin, Mahle…), y «+ taller o
 * servicio» haría dudar de dónde se carga una de ésas. */
const SINGULAR = {
  Tractores: "tractor",
  Furgones: "furgón",
  Vehículos: "vehículo",
  Personas: "persona",
  Proveedores: "proveedor",
  "Talleres y servicios": "proveedor",
  Stock: "ítem",
  Asignaciones: "asignación",
};

const tituloSingular = (t) => {
  const s = String(t || "").trim();
  return SINGULAR[s] || s.toLowerCase() || "registro";
};

// columnas de identificador (DNI, interno, dominio...): van en
// monoespaciada para separarlas del texto corrido, sin recuadro
const ES_COLUMNA_IDENTIFICADORA = (key) => key === "id" || key === "dominio";

// La columna de RTO del padrón (tractores/furgones). La pega `Padron.jsx` con
// `rtoTexto` (la etiqueta) y `rtoTono` (el color del semáforo), y acá se dibuja
// con el mismo puntito que el RTO usaba en su pantalla propia.
const ES_COLUMNA_RTO = (key) => key === "rtoTexto";

/* La celda que se puede tocar con el lápiz prendido.
 *
 * ── EL CLIC NO PUEDE ABRIR LA FICHA ───────────────────────────────────────
 *
 * La fila entera tiene un `onClick` que abre el panel lateral, y ÉSE era el
 * problema que reportó Igna: con el lápiz prendido, ir a corregir una celda
 * abría el popup encima. Por eso el `stopPropagation` no es una prolijidad —
 * es literalmente el arreglo.
 *
 * ── SE GUARDA AL SALIR DEL CAMPO, Y SÓLO SI CAMBIÓ ───────────────────────
 *
 * Igual que en Documentos. Un botón de guardar por celda serían dos clics más
 * en la pantalla donde hay que completar seiscientos huecos, y guardar en cada
 * tecla escribiría una fila por letra.
 *
 * ── Y SI NO SE PUDO, EL VALOR VUELVE ─────────────────────────────────────
 *
 * Es lo que hace que un error se VEA. Dejando lo tipeado en pantalla, la celda
 * diría el valor nuevo mientras la base tiene el viejo: la pantalla mentiría
 * hacia el lado peor —«se guardó»— que es exactamente lo que le pasó a la
 * pantalla de Documentos cuando la clave estaba mal.
 */
const CeldaEditable = ({ coleccion, fila, campo, claveId, onError }) => {
  const original = fila[campo] ?? "";
  const [valor, setValor] = useState(original);
  const [yendo, setYendo] = useState(false);

  // Si la fila cambió por realtime —otro la tocó— la celda tiene que seguirla,
  // salvo mientras se está escribiendo adentro.
  useEffect(() => {
    setValor(fila[campo] ?? "");
  }, [fila, campo]);

  const guardar = async () => {
    const nuevo = cambio(original, valor);
    if (nuevo === null) return;
    setYendo(true);
    try {
      await updateRow(coleccion, fila[claveId], { [campo]: nuevo });
    } catch (e) {
      setValor(original);
      onError?.(e.message || "No se pudo guardar");
    } finally {
      setYendo(false);
    }
  };

  return (
    <input
      className="celda-editable"
      value={valor}
      disabled={yendo}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValor(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => {
        // Enter guarda y Escape cancela: es lo que espera cualquiera que
        // viene de una planilla, y sin Escape la única salida de un tipeo a
        // medias sería borrarlo a mano.
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setValor(original);
          e.currentTarget.blur();
        }
      }}
    />
  );
};

const claseDeCelda = (col, claveId) =>
  ES_COLUMNA_ESTADO(col.key)
    ? "celda--estado"
    : col.key === claveId
      ? "celda--id"
      : "celda--sec";

// Un valor que no esta cargado no ocupa lugar en la tarjeta de celular: en la
// tabla una celda vacia se entiende por la columna, suelta en una tarjeta seria
// un guion perdido en el medio de una frase.
const estaVacio = (v) =>
  v === undefined || v === null || v === "" || v === "-";

const Modal = ({
  title,
  coleccion,
  subcoleccion,
  index = "id",
  data = [],
  headers = null,
  filtroSector,
  filtroParaForm = null,
  reload = null,
  ficha = true, // para activar o bloquear la ficha emergente al hacer click en item
  editable = true, // para activar o bloquear el boton "editar" en las fichas
  /* Y LAS CELDAS SE PRENDEN APARTE, con su propio parámetro y en `false`.
   *
   * `editable` viene en `true` por default y son SEIS pantallas las que lo
   * dejan así sin haberlo pedido —Tractores, Furgones, Vehículos, Personas,
   * Stock y Empresas, todas las de `components/access`—. Colgando las celdas
   * de ese mismo booleano, esas seis pasaban a tener toda la tabla en modo
   * escritura para cualquiera que la abriera, sin lápiz que lo anunciara.
   *
   * Y ahí está la diferencia: `editable` habilita el botón EDITAR de la ficha,
   * que es un paso más y un formulario; esto convierte cada celda en un campo
   * de texto. En un padrón de 658 filas eso es una corrección de más esperando
   * a pasar — es el argumento que `Padron.jsx` ya escribió para el lápiz.
   *
   * Por eso lo prende SÓLO el padrón, que es donde el lápiz existe. */
  celdasEditables = false,
  /* AGREGAR NO ES EDITAR, Y NO VA DETRÁS DEL LÁPIZ.
   *
   * El botón «+ persona» colgaba de `editable`, o sea del lápiz, igual que la
   * edición de celdas. Y son dos cosas distintas: el lápiz existe para que un
   * clic de más sobre una fecha no se guarde solo, y agregar a alguien ya
   * tiene su propio formulario, sus campos obligatorios y su botón de
   * confirmar — nadie da de alta a una persona sin querer.
   *
   * Lo que costó: Natalia, Vilte, Erick y Nico tienen los cuatro
   * `cargar.gente`, y tres de ellos pidieron «que nos den el botón» porque no
   * lo encontraban. No faltaba ningún permiso: faltaba que el botón se viera.
   * Un permiso que está puesto y no se usa es lo mismo que no tenerlo.
   *
   * Cae en `editable` cuando no se lo pasa, así que las ocho pantallas que no
   * lo mandan siguen exactamente igual. */
  puedeAgregar,
  // Cuando es una pantalla del rail y no un modal: sin la capa oscura de
  // fondo, sin el botón de cerrar y sin el título repetido (ya está en la
  // cabecera del hub). El contenido —la tabla, la ficha, los filtros— es el
  // mismo, y tiene que seguir siéndolo: son dos formas de ver lo mismo, no dos
  // componentes que se van a ir separando con el tiempo.
  comoPagina = false,
  /* Con qué texto abre la búsqueda. Lo manda el buscador del hub cuando el
   * resultado es una persona: sin esto, el resultado llevaría al padrón
   * entero y habría que buscarla otra vez a mano. */
  buscarInicial = "",
  /* La fila que pidió el buscador maestro: lo que devuelve `useItemPedido`
   * (`{ ver, resaltar, listo }`). Lo pasa el padrón y nadie más: leer `?ver=`
   * acá adentro haría que esta misma tabla, abierta como overlay desde otra
   * pantalla, saltara a una fila con un id que era para la pantalla de abajo.
   * Ver el bloque «LA FILA QUE PIDIÓ EL BUSCADOR» más abajo. */
  itemPedido = null,
  onClose,
}) => {
  console.log(`----------- Render Modal ${title}`);
  const navigate = useNavigate();
  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [formAgregarVisible, setFormAgregarVisible] = useState(false);

  const rows = data ?? [];
  const columnas = headers ?? Object.keys(rows?.[0] ?? {});

  // Cual es el dato que encabeza cada tarjeta en celular. El primer
  // identificador de la tabla; si el listado no tiene ninguno, la primera
  // columna, que es la que igual estaria mirando primero.
  /* UN ERROR AL GUARDAR SE DICE, y arriba de la tabla y no en la celda: con
   * veinte columnas, un cartel adentro de una la parte al medio y se lee
   * peor justo cuando importa. El valor además vuelve al original, así que la
   * pantalla nunca muestra algo que la base no tiene. */
  const [errorCelda, setErrorCelda] = useState(null);

  /* ── SI ESTA FILA TIENE FICHA PROPIA, EL POPUP NO VA ────────────────────
   *
   * Lo dijo Igna: *«ese popup no debería existir, nunca. Si apretás en el
   * número de interno te debería llevar a la vista de tarjetas»*.
   *
   * Y no es sólo estética: eran DOS pantallas para la misma unidad. El panel
   * lateral era el único que escribía y la ficha completa era la única que
   * mostraba el taller, el RTO, el último cruce y la ubicación — así que había
   * que abrir las dos, y ninguna contestaba sola «¿qué pasa con el 774?».
   *
   * `tipoDeColeccion` devuelve `tractor`/`furgon` y `null` para el resto:
   * `personas` ya tiene su `/persona/:dni`, y `proveedores`, `stock`,
   * `asignaciones` y `eventos` **no tienen ficha propia todavía**, así que ahí
   * el panel se queda. Sacárselo sin tener a dónde llevarlos dejaría esos
   * cuatro padrones sin ninguna forma de mirar una fila. */
  const tipoUnidad = tipoDeColeccion(coleccion);

  const claveId =
    columnas.find((c) => ES_COLUMNA_IDENTIFICADORA(c.key))?.key ??
    columnas[0]?.key;

  const {
    textoFiltro,
    setTextoFiltro,
    facetas,
    filtroSelect,
    setSelect,
    filtroScope,
    toggleScope,
    dataFiltrada,
    hayFiltrosActivos,
    limpiarFiltros,
    resumen,
    columnaEstado,
  } = useFiltrosTabla({ rows, headers, textoInicial: buscarInicial });

  /* ── EL ORDEN, AL TOCAR EL TÍTULO DE LA COLUMNA ──────────────────────────
   *
   * Igual que en «Choferes» y «Nómina»: un toque ordena, otro lo da vuelta y
   * el tercero lo deja como venía. Se aplica AL FINAL, sobre lo que ya se
   * filtró: el filtro y la búsqueda deciden QUIÉNES, y esto en qué orden — por
   * eso el conteo y el resumen de arriba no se enteran.
   *
   * No se recuerda entre visitas, como el lápiz. */
  const [orden, setOrden] = useState(SIN_ORDEN);
  const filasOrdenadas = useMemo(
    () => ordenarFilas(dataFiltrada, orden),
    [dataFiltrada, orden],
  );

  /* ── LA FILA QUE PIDIÓ EL BUSCADOR ───────────────────────────────────────
   *
   * Igna, 02/10: «que se abra en el ítem exacto, no solo la pantalla». El
   * buscador maestro manda al padrón con `?buscar=` (que filtra, como antes)
   * y además `?ver=<id>`, que dice CUÁL de lo que queda es: buscando «filtro»
   * en el stock quedan veinte filas, y la que se eligió en el hub es una.
   *
   * ABRIR ES LO MISMO QUE TOCAR LA FILA. Donde la fila abre el panel lateral
   * (stock, proveedores: los padrones sin ficha propia) se abre el panel, y la
   * fila queda centrada y titilando detrás. Donde la fila NAVEGA a la ficha de
   * la unidad no se navega: el buscador ya manda a esas unidades directo a su
   * ficha, y saltar de pantalla sin que nadie toque nada desorienta. Ahí, y
   * con el lápiz prendido, sólo se resalta.
   *
   * SI LOS FILTROS LA TAPAN, SE BUSCA POR SU CÓDIGO. Con `?buscar=` el texto
   * coincide casi siempre; si no (el nombre cambió desde que el buscador lo
   * listó, o un filtro por defecto la deja afuera), la búsqueda pasa a ser el
   * id, que la trae seguro — la búsqueda le gana al filtro (ver
   * `useFiltrosTabla`).
   *
   * SI NO ESTÁ, SE DICE. Pero sólo con la tabla cargada: el padrón llega por
   * realtime y arranca vacío, y «no la encontré» sobre una lista que todavía
   * no llegó sería mentir. Lista vacía = esperar. */
  const { ver = "", resaltar, listo } = itemPedido || {};
  const cuerpoRef = useRef(null);
  const [noEsta, setNoEsta] = useState("");
  // Sin useMemo: `find` devuelve el mismo objeto de `rows`, así que como
  // dependencia del efecto es estable igual.
  const filaPedida = ver
    ? rows.find((r) => r?.id != null && String(r.id) === ver) || null
    : null;

  useEffect(() => {
    if (!ver) return;
    if (!filaPedida) {
      if (rows.length > 0) {
        setNoEsta(ver);
        listo?.();
      }
      return;
    }
    setNoEsta("");
    if (!dataFiltrada.includes(filaPedida)) {
      if (textoFiltro !== ver) setTextoFiltro(ver);
      return; // vuelve a correr con la fila ya en la tabla
    }
    resaltar?.(buscarFila(ver, cuerpoRef.current));
    if (!tipoUnidad && ficha && !celdasEditables) setItemSeleccionado(filaPedida);
    listo?.();
    // `rows` y `setTextoFiltro` cambian de identidad en cada render: con
    // `filaPedida` y `dataFiltrada` alcanza para saber si algo cambió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ver, filaPedida, dataFiltrada, textoFiltro, tipoUnidad, ficha,
      celdasEditables, resaltar, listo]);

  const handleRefresh = async () => {
    await refreshCache(coleccion);
    setFormAgregarVisible(false);
  };

  const handleCloseFicha = async () => {
    if (reload) await reload();
    setItemSeleccionado(null);
  };

  if (!data) return <Loading />;

  return (
    <div className={comoPagina ? "listado-pagina" : "modal"}>
      <div className={comoPagina ? "listado-contenido" : "modal-content-2"}>
        {!comoPagina && <CloseButton onClose={onClose} />}
        <div className="modal-header">
          {!comoPagina && <h1 className="modal-title">{title}</h1>}
        </div>

        <ResumenBar
          resumen={resumen}
          filtroSelect={filtroSelect}
          setSelect={setSelect}
        />

        <FiltrosBar
          textoFiltro={textoFiltro}
          setTextoFiltro={setTextoFiltro}
          facetas={facetas}
          filtroSelect={filtroSelect}
          setSelect={setSelect}
          filtroScope={filtroScope}
          toggleScope={toggleScope}
          hayFiltrosActivos={hayFiltrosActivos}
          limpiarFiltros={limpiarFiltros}
          cuenta={
            dataFiltrada.length === rows.length
              ? `${rows.length} filas`
              : `${dataFiltrada.length} de ${rows.length}`
          }
          /* El alta sube a la barra. Los eventos siguen afuera: no se dan de
             alta desde un listado, los carga portería o el bot. */
          onAgregar={
            (puedeAgregar ?? editable) && coleccion !== "eventos"
              ? () => setFormAgregarVisible(true)
              : null
          }
          textoAgregar={tituloSingular(title)}
        />

        {celdasEditables && (
          <p className="table-scroll-hint">
            ✎ Estás editando: tocá una celda y se guarda al salir. Enter guarda,
            Escape cancela.
          </p>
        )}
        {/* `suave` y no `.panel-aviso` a secas: el overlay absoluto taparía la
            tabla entera y dejaría la pantalla sin salida justo cuando hay algo
            a medio corregir. */}
        {errorCelda && <p className="panel-aviso suave">{errorCelda}</p>}
        {noEsta && (
          <p className="panel-aviso suave" role="status">
            No encontré «{noEsta}» en {String(title || "este listado").toLowerCase()}
            {" "}(puede haberse dado de baja o cambiado de código).
          </p>
        )}
        <p className="table-scroll-hint">← deslizá para ver más columnas →</p>
        <div className="table-scroll-wrapper">
          <div className="table-body-wrapper">
            <table className="table-lista table-lista--tarjetas">
              <thead className="table-titles">
                <tr>
                  {columnas.map((col) =>
                    /* Una columna sin rótulo (la unidad de medida del stock)
                       no ordena: sería un botón que no se ve. */
                    col.label.trim() ? (
                      <ThOrden
                        key={col.key}
                        columna={col.key}
                        orden={orden}
                        alTocar={(c) => setOrden((o) => siguienteOrden(o, c))}
                      >
                        {col.label.toUpperCase()}
                      </ThOrden>
                    ) : (
                      <th key={col.key}>{col.label.toUpperCase()}</th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="table-doby" ref={cuerpoRef}>
                {filasOrdenadas.map((row, i) => {
                  /* LA FILA ENTERA ABRE LA FICHA, no sólo el número de interno.
                   * Era lo que faltaba: el padrón mostraba los datos y el tap en
                   * cualquier lado no llevaba a ningún lado, mientras el viejo
                   * «RTO de …» sí abría la ficha — al revés de lo esperable.
                   *
                   * Con el lápiz prendido NO navega: ahí se está corrigiendo
                   * celda por celda y un click tiene que quedarse en la celda,
                   * no saltar de pantalla. Las unidades sin ficha propia
                   * (`personas`, `stock`…) siguen abriendo el panel lateral. */
                  const rutaFicha =
                    tipoUnidad && !celdasEditables
                      ? rutaDeUnidad(tipoUnidad, row[claveId])
                      : null;
                  const abrir = tipoUnidad
                    ? rutaFicha
                      ? () => {
                          onClose?.();
                          navigate(rutaFicha);
                        }
                      : undefined
                    : () => setItemSeleccionado(row);
                  return (
                  <tr
                    key={i}
                    onClick={abrir}
                    data-ver={row?.id != null ? String(row.id) : undefined}
                    className={
                      (tonoDeFila(row, columnaEstado)
                        ? `fila--${tonoDeFila(row, columnaEstado)}`
                        : "") + (abrir ? " fila--clickable" : "")
                    }
                  >
                    {columnas.map((col) => (
                      // el title es lo que rescata el valor cuando la
                      // columna es más angosta que el dato y se corta
                      <td
                        key={col.key}
                        className={claseDeCelda(col, claveId)}
                        data-vacio={
                          estaVacio(renderizarValor(row[col.key], col.key))
                            ? "1"
                            : undefined
                        }
                        title={renderizarValor(row[col.key], col.key, "upper")}
                      >
                        {ES_COLUMNA_RTO(col.key) ? (
                          /* El semáforo del RTO, el mismo puntito que usaba su
                             pantalla propia. Sin mayúsculas: «Al día», no «AL
                             DÍA». */
                          <span className="rto-celda">
                            <span
                              className={
                                "rto-punto rto-punto--" +
                                (row.rtoTono || "sin_consultar")
                              }
                            />
                            {row.rtoTexto || "—"}
                          </span>
                        ) : ES_COLUMNA_ESTADO(col.key) ? (
                          <EstadoPill valor={row[col.key]} declarado={row.estadoDeclarado} />
                        ) : celdasEditables && sePuedeTocar(col, claveId) ? (
                          <CeldaEditable
                            coleccion={coleccion}
                            fila={row}
                            campo={col.key}
                            claveId={claveId}
                            onError={setErrorCelda}
                          />
                        ) : tipoUnidad && col.key === claveId ? (
                          /* EL INTERNO ES LA PUERTA. Un enlace de verdad y no
                             un `span` con `onClick`: se abre en otra pestaña
                             con el botón del medio, se copia la dirección y el
                             teclado lo alcanza con Tab. */
                          <Link
                            className="table-id table-id--link"
                            to={rutaDeUnidad(tipoUnidad, row[col.key])}
                            onClick={(e) => {
                              e.stopPropagation();
                              /* Y CIERRA EL MODAL AL IRSE. Esta misma tabla se
                                 abre como overlay desde las baldosas de
                                 `access`: sin esto la ruta cambia por abajo y
                                 la capa oscura queda tapando la ficha a la que
                                 se acaba de entrar. En el padrón `onClose` no
                                 hace nada, que es lo correcto — ahí la tabla
                                 es la pantalla. */
                              onClose?.();
                            }}
                          >
                            {renderizarValor(row[col.key], col.key, "upper")}
                          </Link>
                        ) : col.key === "dominio" &&
                          !estaVacio(renderizarValor(row[col.key], col.key)) ? (
                          /* LA PATENTE COMO CHAPA MERCOSUR, igual que en la
                             tabla de las colecciones (Igna, 03/10: «quedan re
                             facheras»). Va después del lápiz: con la edición
                             prendida es un campo y se escribe. Vacía no se
                             dibuja: una chapa en blanco no es un dato que
                             falta, es una patente vacía. */
                          <Patente dominio={row[col.key]} />
                        ) : ES_COLUMNA_IDENTIFICADORA(col.key) ? (
                          <span className="table-id">
                            {renderizarValor(row[col.key], col.key, "upper")}
                          </span>
                        ) : (
                          renderizarValor(row[col.key], col.key, "upper")
                        )}
                      </td>
                    ))}
                  </tr>
                  );
                })}
                {dataFiltrada.length === 0 && (
                  <tr>
                    <td colSpan={columnas.length} className="table-vacio">
                      No hay resultados para estos filtros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {itemSeleccionado && ficha && (
        <Ficha
          filtroParaForm={filtroParaForm}
          elemento={itemSeleccionado}
          coleccion={coleccion}
          area={subcoleccion}
          /* Por la COLECCIÓN antes que por el título: el título es un
             rótulo que se puede cambiar (el 20/10 «Proveedores» pasó a ser
             «Talleres y servicios») y la ficha es del dato. El área de la
             fila sigue ganando —los eventos de cada sector tienen la suya— y
             el título queda de repuesto para lo que no tiene ficha propia. */
          container={
            filtroSector
              ? fichaContent[filtroSector.toLowerCase()]
              : (fichaContent[itemSeleccionado.area?.toLowerCase()] ??
                fichaContent[coleccion] ??
                fichaContent[title?.toLowerCase()] ??
                [])
          }
          onClose={handleCloseFicha}
          editable={editable}
        />
      )}
      {formAgregarVisible && (
        <FormGestor
          tipo={coleccion}
          coleccion={filtroSector ? `${coleccion}${filtroSector}` : coleccion}
          area={subcoleccion}
          onClose={handleRefresh}
          reload={reload}
        />
      )}
    </div>
  );
};

export default memo(Modal);
